import { getAnswer } from '@/utils/onboardingStorage'
import { toneVariant } from '@/utils/personalization'

type FeedbackContent = {
  titleKey: string
  titleParams?: Record<string, string | number>
  subtitleKey: string
  subtitleParams?: Record<string, string | number>
}

// ===== NAME FEEDBACK (before companion style is known — stays neutral) =====
export function getNameFeedback(): FeedbackContent {
  const name = getAnswer('name')?.answer || 'friend'
  return {
    titleKey: 'pages.onboarding.nameFeedback.title',
    titleParams: { name },
    subtitleKey: 'pages.onboarding.nameFeedback.subtitle',
  }
}

// ===== COMPANION STYLE FEEDBACK =====
export function getCompanionStyleFeedback(): FeedbackContent {
  const style = getAnswer('companion-style')?.answer_key || 'open'
  return {
    titleKey: `pages.onboarding.companionStyleFeedback.title_${style}`,
    subtitleKey: `pages.onboarding.companionStyleFeedback.subtitle_${style}`,
  }
}

// ===== AGE FEEDBACK =====
export function getAgeFeedback(): FeedbackContent {
  return {
    titleKey: toneVariant('pages.onboarding.ageFeedback.title'),
    subtitleKey: toneVariant('pages.onboarding.ageFeedback.subtitle'),
  }
}

// ===== GOAL FEEDBACK =====
export function getGoalFeedback(): FeedbackContent {
  const goal = getAnswer('goal')?.answer_key || 'friend'
  return {
    titleKey: toneVariant(`pages.onboarding.goalFeedback.title_${goal}`),
    subtitleKey: toneVariant(`pages.onboarding.goalFeedback.subtitle_${goal}`),
  }
}

// ===== FEELINGS FEEDBACK =====
export function getFeelingsFeedback(): FeedbackContent {
  const mood =
    getAnswer('feelings')?.answer_key || getAnswer('mood')?.answer_key || 'okay'

  return {
    titleKey: toneVariant(`pages.onboarding.feelingsFeedback.title_${mood}`),
    subtitleKey: toneVariant(
      `pages.onboarding.feelingsFeedback.subtitle_${mood}`,
    ),
  }
}

// ===== CHALLENGE FEEDBACK =====
export function getChallengeFeedback(): FeedbackContent {
  const challenge = getAnswer('challenge')?.answer_key || 'disconnected'
  return {
    titleKey: toneVariant(
      `pages.onboarding.challengeFeedback.title_${challenge}`,
    ),
    subtitleKey: toneVariant(
      `pages.onboarding.challengeFeedback.subtitle_${challenge}`,
    ),
  }
}

// ===== COMM FEEDBACK =====
export function getCommFeedback(): FeedbackContent {
  const commStyle =
    getAnswer('comm-style')?.answer_key ||
    getAnswer('comm_style')?.answer_key ||
    'mix'
  return {
    titleKey: toneVariant(`pages.onboarding.commFeedback.title_${commStyle}`),
    subtitleKey: toneVariant(
      `pages.onboarding.commFeedback.subtitle_${commStyle}`,
    ),
  }
}

// ===== TOPICS FEEDBACK =====
export function getTopicsFeedback(): FeedbackContent {
  const topicsAnswer = getAnswer('topics')?.answer || ''
  const count = topicsAnswer
    ? topicsAnswer
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length
    : 3
  return {
    titleKey: toneVariant('pages.onboarding.topicsFeedback.title'),
    titleParams: { count },
    subtitleKey: toneVariant('pages.onboarding.topicsFeedback.subtitle'),
    subtitleParams: { count },
  }
}

// ===== FREQUENCY FEEDBACK =====
export function getFrequencyFeedback(): FeedbackContent {
  return {
    titleKey: toneVariant('pages.onboarding.frequencyFeedback.title'),
    subtitleKey: toneVariant('pages.onboarding.frequencyFeedback.subtitle'),
  }
}

// ===== DEALBREAKERS FEEDBACK =====
export function getDealFeedback(): FeedbackContent {
  const dbs = getAnswer('dealbreakers')?.answer || ''
  const hasDealbreakers = dbs.length > 0
  return {
    titleKey: toneVariant('pages.onboarding.dealFeedback.title'),
    subtitleKey: hasDealbreakers
      ? toneVariant('pages.onboarding.dealFeedback.subtitle_with')
      : toneVariant('pages.onboarding.dealFeedback.subtitle_without'),
  }
}
